import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activeTourId: null,
  isRunning: false,
  stepIndex: 0,
  completedTourIds: [],
  dismissedTourIds: [],
  // The `target` selector of the step the tour is currently on, published by
  // `JoyrideTourRunner` as it moves.
  //
  // It is mirrored here rather than looked up from `toursRegistry` because the
  // parts of the app shell that react to it -- `useResetOnTourStep`, and the
  // profile menu that has to open itself when a step points inside it -- are
  // eagerly imported, so a registry import from any of them drags all ~37 KB
  // of tour prose into `main.js` for every user on every page, tour or no
  // tour. The runner already owns the registry and is already lazy, so it is
  // the one place that can hand out the current target for free.
  //
  // The lag between `startTour` and the runner's chunk arriving is only
  // visible to a step 0 that needs its target known before the tour is on
  // screen; no tour has one (the menu steps are 7 and 8 of
  // `getting-started`). Not persisted -- see `productTourPersistConfig`.
  currentStepTarget: null,
  // Whether the "Product Walkthroughs" picker is open. It lives here rather
  // than in the sidebar's local state because the tour runner reopens it when
  // a walkthrough is skipped (see `skipTour`), and the runner is mounted far
  // away from the sidebar. Not persisted -- see `productTourPersistConfig`.
  isWalkthroughsListOpen: false,
};

const productTourSlice = createSlice({
  name: 'productTour',
  initialState,
  reducers: {
    startTour: (state, action) => {
      state.activeTourId = action.payload;
      state.isRunning = true;
      state.stepIndex = 0;
      state.currentStepTarget = null;
      state.isWalkthroughsListOpen = false;
    },
    stopTour: (state) => {
      state.isRunning = false;
      state.activeTourId = null;
      state.stepIndex = 0;
      state.currentStepTarget = null;
    },
    setStepIndex: (state, action) => {
      state.stepIndex = action.payload;
    },
    setCurrentStepTarget: (state, action) => {
      state.currentStepTarget = action.payload || null;
    },
    completeTour: (state, action) => {
      const tourId = action.payload;
      if (tourId && !state.completedTourIds.includes(tourId)) {
        state.completedTourIds.push(tourId);
      }
      state.isRunning = false;
      state.activeTourId = null;
      state.stepIndex = 0;
      state.currentStepTarget = null;
    },
    dismissTour: (state, action) => {
      const tourId = action.payload;
      if (tourId && !state.dismissedTourIds.includes(tourId)) {
        state.dismissedTourIds.push(tourId);
      }
      state.isRunning = false;
      state.activeTourId = null;
      state.stepIndex = 0;
      state.currentStepTarget = null;
    },
    /**
     * The tooltip's "Skip walkthrough" button: ends this walkthrough the same
     * way a dismissal does, then reopens the picker so the user lands back on
     * the list and can move straight on to a different walkthrough. Closing
     * the tour with the "X" instead uses `dismissTour` and leaves them where
     * they are.
     */
    skipTour: (state, action) => {
      const tourId = action.payload;
      if (tourId && !state.dismissedTourIds.includes(tourId)) {
        state.dismissedTourIds.push(tourId);
      }
      state.isRunning = false;
      state.activeTourId = null;
      state.stepIndex = 0;
      state.currentStepTarget = null;
      state.isWalkthroughsListOpen = true;
    },
    setWalkthroughsListOpen: (state, action) => {
      state.isWalkthroughsListOpen = action.payload;
    },
    resetTourProgress: (state, action) => {
      const tourId = action.payload;
      state.completedTourIds = state.completedTourIds.filter(
        (id) => id !== tourId,
      );
      state.dismissedTourIds = state.dismissedTourIds.filter(
        (id) => id !== tourId,
      );
    },
  },
});

export const {
  startTour,
  stopTour,
  setStepIndex,
  setCurrentStepTarget,
  completeTour,
  dismissTour,
  skipTour,
  setWalkthroughsListOpen,
  resetTourProgress,
} = productTourSlice.actions;

export default productTourSlice.reducer;
